import { useCallback, useContext, useEffect, useMemo } from 'react'
import { useLocation } from 'react-router-dom'

import ModalContext from 'contexts/ModalContext'
import {
  getLanguagePreferenceFromAuth,
  updateCustomerLanguagePreferenceFromAuth,
} from 'api/auth'
import { syncUserDetails } from 'actions/AuthActions'
import { SET_REQUEST_LOCALE } from 'actions/actionConstants'
import { useAppDispatch, useAppSelector } from 'hooks/reduxHooks'
import { selectLoggedIn } from 'selectors/accountSelectors'
import { getVisibleLanguageOptions, resolveLocale } from 'i18n/util/locale'
import { useLabRemoteConfig } from 'hooks/FeatureFlag/useLabRemoteConfig'
import { LabRemoteConfigKeys } from 'constants/lab'
import setLanguageCookie from 'cookies/setLanguageCookie'
import * as isomorphicSessionStorage from 'lib/storage/isomorphicSessionStorage'
import { reportClientError } from 'services/errorReportingService'
import LocaleRedirectModal from 'components/Common/Modals/LocaleRedirectModal/LocaleRedirectModal'
import UrlLocation from 'src/lib/url/UrlLocation'

/**
 * Reconciles the authenticated customer language preference with the URL locale
 * after hydration. Clean URLs stay clean for auth-only preferences, explicit URL
 * locales remain the active page-language signal, URL-only preferences are
 * written back to auth, and conflicts are resolved through a one-per-session
 * confirmation modal.
 */
export default function AuthBackedLocalePreferenceSync() {
  const dispatch = useAppDispatch()
  const showModal = useContext(ModalContext)

  const languagesRemoteConfig = useLabRemoteConfig(LabRemoteConfigKeys.languages)
  const languageOptions = useMemo(
    () => getVisibleLanguageOptions(languagesRemoteConfig),
    [languagesRemoteConfig],
  )

  const location = useLocation()
  const urlLocation = useMemo(() => UrlLocation.fromLocation(location), [location])
  const isLoggedIn = useAppSelector(selectLoggedIn)
  const isSpoofed = useAppSelector(state => state.auth.account.isSpoofed)
  const accountLocale = useAppSelector(state => state.auth.account.language)
  const currentLocale = useAppSelector((state) => state.geo.locale)

  const urlLocale = useMemo(
    () => resolveLocale(urlLocation.locale, languageOptions),
    [urlLocation.locale, languageOptions],
  )
  const currentLocaleState = useMemo(
    () => resolveLocale(currentLocale, languageOptions),
    [currentLocale, languageOptions],
  )
  const accountLocaleState = useMemo(
    () => resolveLocale(accountLocale, languageOptions),
    [accountLocale, languageOptions],
  )

  const navigateToLocale = useCallback(
    (locale: string) => {
      const redirect = urlLocation.getRedirectToLocale(
        locale,
        languageOptions,
      )
      const targetUrl = urlLocation.withRedirect(redirect)
      if (!targetUrl.equals(urlLocation)) {
        window.location.assign(targetUrl.toString())
      }
    },
    [urlLocation, languageOptions],
  )

  const resolveLocaleConflict = useCallback(
    async(authLocale: string) => {
      if (!urlLocale.full) return

      const conflictSessionKey = `auth-backed-locale-conflict:${urlLocale.full}:${authLocale}`

      if (isomorphicSessionStorage.get(conflictSessionKey)) return
      isomorphicSessionStorage.set(conflictSessionKey, true)

      try {
        const choice = await showModal<'url' | 'auth'>(
          <LocaleRedirectModal urlLocale={urlLocale.full} accountLocale={authLocale} />,
        )
        if (choice === 'url' && urlLocation.locale) {
          await updateCustomerLanguagePreferenceFromAuth(urlLocale.full)
          dispatch(syncUserDetails({ language: urlLocale.full }))
          setLanguageCookie(urlLocale.full)
          dispatch({ type: SET_REQUEST_LOCALE, locale: urlLocale.full })
          navigateToLocale(urlLocation.locale)
        } else if (choice === 'auth') {
          dispatch({ type: SET_REQUEST_LOCALE, locale: authLocale })
          navigateToLocale(authLocale)
        }
      } catch(error) {
        reportClientError(error)
      }
    },
    [navigateToLocale, urlLocation.locale, urlLocale.full, showModal, dispatch],
  )

  useEffect(() => {
    if (isSpoofed) return

    const abortController = new AbortController()
    const { signal } = abortController

    async function run() {
      if (signal.aborted) return

      if (isLoggedIn && !accountLocale) {
        const freshAccountLocale = await getLanguagePreferenceFromAuth(signal)
        if (signal.aborted) return

        const freshAccount = resolveLocale(freshAccountLocale, languageOptions)

        if (freshAccount.full) {
          if (freshAccount.normalised !== urlLocale.normalised && urlLocale.full) {
            await resolveLocaleConflict(freshAccount.full)
          }
        } else if (urlLocale.full) {
          dispatch(syncUserDetails({ language: urlLocale.full }))
        }
      } else if (isLoggedIn && accountLocale) {
        if (!accountLocaleState.full) return

        if (urlLocale.full && accountLocaleState.normalised !== urlLocale.normalised) {
          await resolveLocaleConflict(accountLocaleState.full)
        } else if (!urlLocale.full && accountLocaleState.normalised !== currentLocaleState.normalised) {
          dispatch({ type: SET_REQUEST_LOCALE, locale: accountLocaleState.full })
          setLanguageCookie(accountLocaleState.full)
          navigateToLocale(accountLocaleState.full)
        }
      } else if (urlLocale.full && urlLocale.normalised !== currentLocaleState.normalised) {
        navigateToLocale(urlLocale.full)
      }
    }

    run()

    return () => {
      abortController.abort()
    }
  }, [
    dispatch,
    isSpoofed,
    isLoggedIn,
    languageOptions,
    accountLocale,
    urlLocale,
    currentLocaleState,
    accountLocaleState,
    navigateToLocale,
    resolveLocaleConflict,
  ])

  return null
}
